import { Stack, Tooltip, Typography } from '@mui/material';
import { createStyles, makeStyles } from '@mui/styles';
import { GridColDef, GridRowSelectionModel, GridSortModel } from '@mui/x-data-grid-premium';
import { format } from 'date-fns';
import React, { useCallback, useMemo, useState } from 'react';
import { generatePath, useNavigate } from 'react-router-dom';

import { ADMIN_CONTRACT_EDIT_URL, ADMIN_CONTRACT_REPORTS_URL } from '../../constants/routeUrls';
import { useConfig } from '../../hooks/useConfig';
import { formatServerDate, mmddyy } from '../../util/helpers';
import { KpiPerformance, riskCount } from '../../util/kpiPerformance';
import DataGrid from '../shared/DataGrid';
import StatusChip from '../shared/StatusChip';
import ContractEditPageLink from './ContractEditPageLink';
import ContractLatestReportButton from './ContractLatestReportButton';
import ContractReportsPageLink from './ContractReportsPageLink';
import { useContractsContext } from './ContractsContext';
import KpiPerformanceChips from './KpiPerformanceChips';
import PacingSparkline from './PacingSparkline';
import { ContractsTableContext } from './useContractsTableContext';

const COLUMN_WIDTHS_KEY = 'contractsTableColumnWidths';

export enum ContractListMode {
  Default,
  SelectOnly,
}

export const headerStyles = makeStyles(() =>
  createStyles({
    root: {
      borderRadius: '10px',
      border: '1px solid #D9D9D9',
      background: '#FFF',
      boxShadow: '0px 0px 0px 1px #E0E0E0',
      '& table': {
        margin: '24px',
      },
      '& tbody tr:hover': {
        cursor: 'pointer',
        backgroundColor: '#f2f2f2',
      },
      '& .MuiChip-filled': {
        color: 'white',
      },
    },
  })
);

interface ContractsTableProps {
  contractData: any;
  kpiByContractId?: Record<string, KpiPerformance[]>;
  rowCount: number;
  paginationModel: { page: number; pageSize: number };
  setPaginationModel: (params: { page: number; pageSize: number }) => void;
  isLoading: boolean;
  mode?: ContractListMode;
  value?: any[];
  onChange?: Function;
  onRequestSort: (orderBy, order) => void;
}

function ContractNameCell({ params }: { params: any }) {
  const { apiHost } = useConfig();
  const navigate = useNavigate();
  const [, setHover] = useState<boolean>(false);

  const { row: contract } = params;

  const onMouseOver = () => setHover(true);
  const onMouseOut = () => setHover(false);

  const handleRowClick = (e, contractId, status) => {
    if (e.button === 1) {
      window.open(`${apiHost}/admin#/contracts/${contractId}`, '_blank');
    }

    const contractReportsPageUrl = generatePath(ADMIN_CONTRACT_REPORTS_URL, { id: contractId.toString() });

    if (e.metaKey || e.ctrlKey) {
      window.open(`${apiHost}/admin#/contracts/${contractId}`, '_blank');
    } else {
      if (status === 'spending') {
        navigate(contractReportsPageUrl);
        return;
      }
      navigate(generatePath(ADMIN_CONTRACT_EDIT_URL, { id: contractId }));
    }
  };

  return (
    <Stack
      onMouseOver={onMouseOver}
      onMouseOut={onMouseOut}
      onClick={(e) => handleRowClick(e, contract.id, contract.derived_ui_state)}
      sx={{
        cursor: 'pointer',
      }}
    >
      <Tooltip
        title={contract.derived_ui_state === 'spending' ? 'Performance Report' : 'Edit Contract'}
        placement="top-start"
      >
        <Stack>
          {contract.name} ({contract.id})
        </Stack>
      </Tooltip>
    </Stack>
  );
}

function ContractActionCell({ params }: { params: any }) {
  const { row: contract, api, id } = params;
  const index = api.getRowIndexRelativeToVisibleRows(id);

  return (
    <Stack
      direction="row"
      spacing={1}
      sx={{
        cursor: 'pointer',
        lineHeight: 0,
      }}
    >
      <ContractLatestReportButton contractId={contract.id} index={index} compact />
      {contract.derived_ui_state !== 'spending' && <ContractReportsPageLink contractId={contract.id} compact />}
      {contract.derived_ui_state === 'spending' && <ContractEditPageLink contractId={contract.id} compact />}
    </Stack>
  );
}

// 5-band color by distance from 100% pacing (ADOPS-2646).
const PACING_BANDS = {
  onGoal: { fill: '#2E7D32', track: '#D4EDDA' }, // ≤5%
  nearGoal: { fill: '#7EBE42', track: '#E6F4D7' }, // ≤15%
  warn: { fill: '#F9A825', track: '#FFF4D1' }, // ≤30%
  concern: { fill: '#EF6C00', track: '#FFE0B2' }, // ≤50%
  alarm: { fill: '#C62828', track: '#FFCDD2' }, // >50%
};

function bandFor(percent: number) {
  const distance = Math.abs(percent - 100);
  if (distance <= 5) return PACING_BANDS.onGoal;
  if (distance <= 15) return PACING_BANDS.nearGoal;
  if (distance <= 30) return PACING_BANDS.warn;
  if (distance <= 50) return PACING_BANDS.concern;
  return PACING_BANDS.alarm;
}

// ─────────────────────── 5-Day Pacing Sparkline helpers (ADOPS-2647) ───────────────────────

// daily_spend dates arrive as "YYYYMMDD" strings (EST). budget_by_day.budget_date is a
// date/ISO-ish string from Postgres. Normalize both into the same "YYYYMMDD" key so we
// can co-locate impressions and goals by day.
function normalizeDateKey(value: any): string | null {
  if (!value) return null;
  if (typeof value === 'string' && /^\d{8}$/.test(value)) return value;
  const date = formatServerDate(value);
  if (!date) return null;
  return format(date, 'yyyyMMdd');
}

// "Today" anchored to America/New_York — data is reported in EST and ad-ops users span timezones.
function todayInEstParts(): { year: number; month: number; day: number } {
  const fmt = new Intl.DateTimeFormat('en-CA', {
    timeZone: 'America/New_York',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  });
  const parts = fmt.formatToParts(new Date());
  const pick = (type: string) => Number(parts.find((p) => p.type === type)?.value ?? 0);
  return { year: pick('year'), month: pick('month'), day: pick('day') };
}

interface FiveDaySeries {
  values: number[]; // index 0 = 4 days ago, index 4 = today; ratio = imps / goal (0 if no goal)
  impressions: number[]; // same orientation, raw impressions for tooltip
}

function buildFiveDaySeries(impressionsByDate: Map<string, number>, goalsByDate: Map<string, number>): FiveDaySeries {
  const today = todayInEstParts();
  const values: number[] = [];
  const impressions: number[] = [];
  for (let i = 4; i >= 0; i -= 1) {
    // new Date(y, m-1, d-i) does the date arithmetic in local time; format extracts local
    // components, so the key reflects the EST calendar day we asked for regardless of the
    // user's timezone.
    const day = new Date(today.year, today.month - 1, today.day - i);
    const key = format(day, 'yyyyMMdd');
    const imps = impressionsByDate.get(key) || 0;
    const goal = goalsByDate.get(key) || 0;
    impressions.push(imps);
    values.push(goal > 0 ? imps / goal : 0);
  }
  return { values, impressions };
}

function lineItemFiveDay(li: any): FiveDaySeries {
  const impressionsByDate = new Map<string, number>();
  (li?.daily_spend || []).forEach((ds: any) => {
    if (ds?.date && ds.date !== '00000000') {
      impressionsByDate.set(ds.date, (impressionsByDate.get(ds.date) || 0) + (ds.impressions || 0));
    }
  });
  const goalsByDate = new Map<string, number>();
  (li?.budget_by_day || []).forEach((bd: any) => {
    const key = normalizeDateKey(bd?.budget_date);
    if (key) goalsByDate.set(key, (goalsByDate.get(key) || 0) + (bd.budget || 0));
  });
  return buildFiveDaySeries(impressionsByDate, goalsByDate);
}

// Contract roll-up: sum impressions and goals across all line items per day, then ratio.
// Averaging the per-line-item ratios would be wrong — heavier line items should dominate.
function contractFiveDay(contract: any): FiveDaySeries {
  const impressionsByDate = new Map<string, number>();
  const goalsByDate = new Map<string, number>();
  (contract?.line_items_header || []).forEach((li: any) => {
    (li?.daily_spend || []).forEach((ds: any) => {
      if (ds?.date && ds.date !== '00000000') {
        impressionsByDate.set(ds.date, (impressionsByDate.get(ds.date) || 0) + (ds.impressions || 0));
      }
    });
    (li?.budget_by_day || []).forEach((bd: any) => {
      const key = normalizeDateKey(bd?.budget_date);
      if (key) goalsByDate.set(key, (goalsByDate.get(key) || 0) + (bd.budget || 0));
    });
  });
  return buildFiveDaySeries(impressionsByDate, goalsByDate);
}

// Sum one numeric field over a BigQuery-sourced series. The series is absent until the
// lazy pacing fetch lands (ACH-bj8d ?spend=deferred), so a non-array degrades to 0.
function sumField(series: any, field: string): number {
  if (!Array.isArray(series)) return 0;
  return series.reduce((total: number, row: any) => total + (Number(row?.[field]) || 0), 0);
}

// One grid row per contract, plus a child row per line item. Every spend input
// (line_items_header and its daily_spend / budget_by_day) comes from BigQuery and is
// absent on a deferred-spend response, so each level degrades to an empty series
// rather than throwing (ACH-bj8d).
export function buildTreeRows(contractData: any[], kpiByContractId: Record<string, KpiPerformance[]> = {}): any[] {
  const rows: any[] = [];
  (contractData || []).forEach((contract) => {
    const lineItems: any[] = Array.isArray(contract?.line_items_header) ? contract.line_items_header : [];
    const sumImpressions = lineItems.reduce((sum, li) => sum + sumField(li?.daily_spend, 'impressions'), 0);
    const sumGoals = lineItems.reduce((sum, li) => sum + sumField(li?.budget_by_day, 'budget'), 0);
    const contractPacing = sumGoals <= 0 ? 0 : sumImpressions / sumGoals;
    const contractDaily = contractFiveDay(contract);
    const contractKpis = kpiByContractId[String(contract.id)] ?? [];
    rows.push({
      ...contract,
      impressions: sumImpressions,
      pacing: contractPacing,
      dailyPacing: contractDaily.values,
      dailyImpressions: contractDaily.impressions,
      kpiPerformance: contractKpis,
      risk: riskCount(contractKpis),
      path: [String(contract.id)],
      isContract: true,
    });

    lineItems.forEach((li) => {
      const lSumImpressions = sumField(li?.daily_spend, 'impressions');
      const lSumGoals = sumField(li?.budget_by_day, 'budget');
      const lineItemPacing = lSumGoals <= 0 ? 0 : lSumImpressions / lSumGoals;
      const liDaily = lineItemFiveDay(li);
      rows.push({
        id: `li-${li.id}`,
        path: [String(contract.id), String(li.id)],
        isContract: false,
        line_item: li.name,
        start_date: li.start_date,
        end_date: li.end_date,
        impressions: lSumImpressions,
        pacing: lineItemPacing,
        dailyPacing: liDaily.values,
        dailyImpressions: liDaily.impressions,
        contractId: contract.id,
        lineItemId: li.id,
      });
    });
  });
  return rows;
}

// ────────────────────────────────────────────────────────────────────────────────────────────

function PacingBulletChart({ value }: { value: number }) {
  // Label is uncapped (a 250% pacer reads "250%"); bar fill saturates at 200%.
  const percent = Math.round(value * 100);
  const fillPct = Math.min(Math.max(percent, 0), 200) / 2;
  const { fill, track } = bandFor(percent);
  // Switch label to white once the colored fill reaches the text centerline.
  const labelOnFill = fillPct >= 48;

  return (
    <Stack
      sx={{
        position: 'relative',
        width: '100%',
        height: 20,
        borderRadius: '10px',
        backgroundColor: track,
        overflow: 'hidden',
      }}
    >
      <Stack
        sx={{
          position: 'absolute',
          top: 0,
          left: 0,
          width: `${fillPct}%`,
          height: '100%',
          backgroundColor: fill,
          transition: 'width 0.4s ease',
        }}
      />
      {/* Midpoint marker at 100% pacing (50% of bar width). */}
      <Stack
        sx={{
          position: 'absolute',
          top: 2,
          bottom: 2,
          left: '50%',
          width: '1px',
          backgroundColor: 'rgba(0,0,0,0.55)',
        }}
      />
      <Typography
        variant="caption"
        sx={{
          position: 'absolute',
          top: 0,
          left: 0,
          width: '100%',
          height: '100%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          fontWeight: 700,
          fontSize: '0.7rem',
          lineHeight: 1,
          color: labelOnFill ? '#fff' : 'rgba(0,0,0,0.78)',
          textShadow: labelOnFill ? 'none' : '0 0 2px rgba(255,255,255,0.6)',
          pointerEvents: 'none',
        }}
      >
        {percent}%
      </Typography>
    </Stack>
  );
}

export default function ContractsTable({
  contractData,
  kpiByContractId = {},
  rowCount,
  paginationModel,
  setPaginationModel,
  isLoading,
  mode = ContractListMode.Default,
  value = [],
  onChange,
  onRequestSort,
}: ContractsTableProps) {
  const [rowSelectionModel, setRowSelectionModel] = React.useState<GridRowSelectionModel>(value);
  const { columnHeadings, hiddenColumns } = useContractsContext();

  const treeRows = useMemo(() => buildTreeRows(contractData, kpiByContractId), [contractData, kpiByContractId]);

  const handleRowSelectionModelChange = (newRowSelectionModel: GridRowSelectionModel) => {
    setRowSelectionModel(newRowSelectionModel);
    if (onChange) {
      onChange(newRowSelectionModel);
    }
    const selectedContracts = contractData.filter((contract) => newRowSelectionModel.includes(contract.id));
    if (onChange) {
      onChange(selectedContracts);
    }
  };

  const handleSortModelChange = useCallback(
    (sortModel: GridSortModel) => {
      onRequestSort(sortModel[0]?.field, sortModel[0]?.sort);
    },
    [onRequestSort]
  );

  const columnMeta: GridColDef[] = [
    {
      field: 'name',
      renderCell: (params) => {
        if (params.row.isContract) return <ContractNameCell params={params} />;
        // Expanded child row: show the line item name in lighter weight / secondary color
        // so it reads as a child of the contract above without re-introducing a column.
        return (
          <Typography
            sx={{
              fontSize: '0.82rem',
              color: 'text.secondary',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
            title={params.row.line_item}
          >
            {params.row.line_item}
          </Typography>
        );
      },
      display: 'flex',
      flex: 8,
    },
    mode !== ContractListMode.SelectOnly && {
      field: 'action',
      // Not a contracts column — sorting by it 500s the Rails index (raw ORDER BY
      // interpolation) and no-ops on ACH (outside the sort allowlist). RD-14.
      sortable: false,
      renderCell: (params) => (params.row.isContract ? <ContractActionCell params={params} /> : null),
      display: 'flex',
      flex: 1,
    },
    {
      field: 'pacing',
      // Derived client-side from the lazy-loaded BigQuery spend payload (ACH-bj8d), so
      // the server cannot order by it — same as dailyPacing / kpiPerformance / risk.
      sortable: false,
      renderCell: (params) => {
        if (params.value != null) {
          return <PacingBulletChart value={params.value} />;
        }
        return null;
      },
      display: 'flex',
      flex: 1.5,
    },
    {
      field: 'dailyPacing',
      // Disable server sort on this column — the values are derived client-side from daily_spend / budget_by_day.
      sortable: false,
      renderCell: (params) => {
        const values: number[] | undefined = params.value;
        const impressions: number[] | undefined = params.row.dailyImpressions;
        if (!values || values.length === 0) return null;
        return <PacingSparkline values={values} impressionsPerDay={impressions} />;
      },
      display: 'flex',
      flex: 1.2,
    },
    {
      field: 'kpiPerformance',
      // Client-derived from the KPI-performance endpoint — no server sort.
      sortable: false,
      renderCell: (params) => {
        if (!params.row.isContract) return null;
        return <KpiPerformanceChips kpis={params.value || []} />;
      },
      display: 'flex',
      flex: 3,
    },
    {
      field: 'risk',
      // Count of off-track KPIs; client-derived, so no server sort.
      sortable: false,
      align: 'center',
      headerAlign: 'center',
      renderCell: (params) => {
        if (!params.row.isContract) return null;
        const count = params.value ?? 0;
        if (count === 0) return null;
        return <Typography sx={{ fontSize: '0.85rem', fontWeight: 700, color: '#C62828' }}>{count}</Typography>;
      },
      display: 'flex',
      flex: 0.8,
    },
    {
      field: 'derived_ui_state',
      // Ruby-method column (contract.rb) with no SQL backing — same RD-14 class
      // as `action`: Rails 500s, ACH silently default-orders.
      sortable: false,
      renderCell: (params) => {
        if (!params.row.isContract) return null;
        return <StatusChip status={params.row.derived_ui_state} subStatus={params.row.derived_delivery_status} />;
      },
      display: 'flex',
      flex: 2,
    },
    {
      field: 'start_date',
      renderCell: (params) => (params.value ? mmddyy(formatServerDate(params.value)) : null),
      display: 'flex',
    },
    {
      field: 'end_date',
      renderCell: (params) => (params.value ? mmddyy(formatServerDate(params.value)) : null),
      display: 'flex',
    },
  ];

  const columns =
    columnHeadings
      ?.map((columnHeading, index) => ({
        // Initialize `flex` and `field` defaults
        flex: 1,
        field: '',
        ...columnMeta[index],
        headerName: columnHeading,
        hideable: false,
      }))
      .filter((_, index) => !hiddenColumns.includes(index)) || [];

  const [activeIndex, setActiveIndex] = useState<number>();

  const contractLatestReportContextValue = useMemo(
    () => ({ activeIndex, setActiveIndex }),
    [activeIndex, setActiveIndex]
  );

  return (
    <ContractsTableContext.Provider value={contractLatestReportContextValue}>
      <DataGrid
        treeData
        getTreeDataPath={(row) => row.path}
        groupingColDef={{
          headerName: '',
          width: 40,
        }}
        defaultGroupingExpansionDepth={0}
        columns={columns}
        rows={treeRows}
        rowCount={rowCount}
        checkboxSelection={mode === ContractListMode.SelectOnly}
        isRowSelectable={(params) => params.row.isContract}
        loading={isLoading}
        sortingMode="server"
        onSortModelChange={handleSortModelChange}
        paginationMode="server"
        pageSizeOptions={[5, 10, 25, 50, 100]}
        pagination
        paginationModel={paginationModel}
        onPaginationModelChange={setPaginationModel}
        rowSelectionModel={rowSelectionModel}
        onRowSelectionModelChange={handleRowSelectionModelChange}
        columnWidthStorageKey={COLUMN_WIDTHS_KEY}
      />
    </ContractsTableContext.Provider>
  );
}
